import axios, { AxiosResponse } from 'axios';
import { TokenManager } from '../token-manager';
import { Cache } from '../cache';

// API Configuration
export const API_BASE_URL = process.env['REACT_APP_API_URL'] || 'http://localhost:8000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120 second timeout (remote DB)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and user-scoped cache busting
api.interceptors.request.use(
  (config) => {
    // Use TokenManager for secure token retrieval
    const token = TokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    // Include CSRF token for session auth compatibility
    const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1];
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    // CR-4713 round 2 rework: Send real admin user ID during impersonation
    // for the audit trail. The previous read used obsolete localStorage keys
    // ('original_token', 'original_user_id') that TokenManager no longer
    // writes — it stores the original user object in sessionStorage as
    // 'helix_original_user' and the token as 'helix_original_token'. So the
    // X-Impersonated-By header was never sent in production, the backend's
    // _get_acting_as_user() always returned None, and acting_as_user stayed
    // null on every changelog row. Pull from TokenManager's source of truth.
    if (TokenManager.isImpersonating()) {
      const originalUser = TokenManager.getOriginalUser<{ id?: number }>();
      if (originalUser?.id) {
        config.headers['X-Impersonated-By'] = String(originalUser.id);
      }
    }

    // Add user ID to calendar GET requests for user-scoped browser caching
    // This ensures different users get fresh data (different URL = different cache entry)
    // while same user still benefits from browser caching
    if (config.method?.toLowerCase() === 'get' && config.url?.includes('/calendar/')) {
      const user = TokenManager.getUser<{ id: number }>();
      if (user?.id) {
        config.params = {
          ...config.params,
          _uid: user.id,
        };
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token, cached data, and redirect to login
      TokenManager.clearAll();
      Cache.clearAll();
      Cache.setUserScope(null);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic Types
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Re-export AxiosResponse for convenience
export type { AxiosResponse };

export default api;
